import { Kaggle } from "../../../sdk";
import SuggestionType = Kaggle.Sdk.Datasets.SuggestionType;

// Kernel Actions
type KernelAction =
  | "new-kernel-alternate-button-clicked"
  | "new-kernel-button-clicked"
  | "new-kernel-clicked"
  | "new-kernel-alternate-button-shown"
  | "new-kernel-button-shown"
  | "explore-more-kernels-clicked"
  | "dataset-kernel"
  | "script-shown"
  | "new-kernel-using-this-data-clicked"
  | "edit-kernel"
  | "new-script"
  | "new-notebook";

// Dataset Actions
type DatasetAction =
  | "explore-more-datasets-clicked"
  | "data-tab-download-file"
  | "data-home-download-file"
  | "data-tab-download-all-list"
  | "data-home-download-all-list"
  | "data-tab-shown"
  | "data-home-shown"
  | "datasets-list-viewed"
  | "dataset-uploader-shown"
  | "data-preview-tab-shown"
  | "dataset-shown"
  | "dataset-suggesion-clicked"
  | "dataset-new-version-created";

type DatasetLabel = "convert-csv";

// We record two types of votes.
type VoteAction = "up-voted" | "down-voted";

type AccountAction = "new-account";

// Voting contexts, sent as "event_label" to the gtag() call.
type VoteLabel =
  | "newsfeed"
  | "dataset"
  | "forum-message"
  | "forum-topic"
  | "topic-list"
  | "script";

export function sendKernelEvent(action: KernelAction, suffix?: string) {
  const gaAction = (suffix ? action + "-" + suffix : action) as EventName;

  gtag("event", gaAction, { event_category: "kernel" });
}

export function sendDatasetEvent(action: DatasetAction, suffix?: string) {
  const gaAction = (suffix ? action + "-" + suffix : action) as EventName;
  gtag("event", gaAction, { event_category: "dataset" });
}

export function sendDatasetNewVersionEvent(label?: DatasetLabel) {
  const tmpLabel = (label || "") as DatasetLabel;
  gtag("event", "dataset-new-version-created", {
    event_category: "dataset",
    event_label: tmpLabel,
  });
}

export function sendUpvote(label?: VoteLabel, suffix?: string) {
  const tmpLabel = label || "";
  const gaLabel = (suffix ? tmpLabel + "-" + suffix : tmpLabel) as VoteLabel;

  sendVote("up-voted", gaLabel);
}
export function sendDownvote(label?: VoteLabel, suffix?: string) {
  const tmpLabel = label || "";
  const gaLabel = (suffix ? tmpLabel + "-" + suffix : tmpLabel) as VoteLabel;

  sendVote("down-voted", gaLabel);
}
function sendVote(voteType: VoteAction, label?: VoteLabel) {
  gtag("event", voteType, { event_category: "vote", event_label: label });
}

export function sendAccountEvent(action: AccountAction) {
  gtag("event", action, { event_category: "account" });
}

type AvatarEventAction = "clicked";
type AvatarEventLabel = "newsfeed";
export function sendAvatarEvent(context?: AvatarEventLabel) {
  gtag("event", "clicked", { event_category: "avatar", event_label: context });
}

type NewsfeedEventLabel = number;

export function clickedSuggestion(id: number, type: SuggestionType) {
  const action = (type + "-suggesion-clicked") as EventName;
  gtag("event", action, {
    event_category: toEventCategory(type),
    event_label: id,
  });
}

function toEventCategory(type: SuggestionType): EventCategory | undefined {
  switch (type) {
    case SuggestionType.CATEGORY:
      return "category";
    case SuggestionType.COMPETITION:
      return "competition";
    case SuggestionType.DATASET:
      return "dataset";
    case SuggestionType.KERNEL:
      return "kernel";
  }

  return undefined;
}

/**
 * Sets custom dimension defined in Google Analytics dashboard. Current mapping:
 * dimension1: turbolinks - Whether Turbolinks is enabled (legacy dimension)
 */
export function setDimension(index: number, name: string) {
  gtag("set", {
    custom_map: { [`dimension${index}`]: name },
  });
}

/**
 * Sets custom metric defined in Google Analytics dashboard. Current mapping:
 * metric1: timeToReactRender - Time between receiving HTML and React render
 */
export function setMetric(index: number, value: string | number) {
  gtag("set", {
    custom_map: { [`metric${index}`]: value },
  });
}

export function registerPageview(track: string) {
  gtag("set", { page_path: track });
  gtag("event", "page_view");
}

// Basic typings for the gtag.js API
type EventCategory =
  | "competition"
  | "kernel"
  | "dataset"
  | "vote"
  | "avatar"
  | "newsfeed"
  | "category"
  | "account";
type EventName =
  | KernelAction
  | DatasetAction
  | VoteAction
  | AvatarEventAction
  | AccountAction
  | "page_view";
type EventLabel = VoteLabel | NewsfeedEventLabel | DatasetLabel;

interface EventParameters {
  [key: string]: any;
  event_category?: EventCategory;
  event_label?: EventLabel;
}

interface SetParameters {
  [key: string]: any;
}

type EventAction = (
  command: "event",
  eventName: EventName,
  eventParameters?: EventParameters
) => void;

type SetAction = (command: "set", setParameters: SetParameters) => void;

type GoogleAnalytics = EventAction & SetAction;

declare let gtag: GoogleAnalytics;
